import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  Provider,
  SimpleChanges
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { AlertComponent, ButtonComponent, FormGroupComponent, LoaderComponent, RadioComponent } from 'mbs-ui-kit';
import { GuidEmpty } from 'mbs-ui-kit/utils/constants';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { ComputerBackupFacade } from 'mbs-ui-shared@facades/computer.backup.facade';
import { RemoteDeployDataFacade } from 'mbs-ui-shared@facades/remote-deploy-data.facade';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { finalize, of, switchMap } from 'rxjs';
import { Storages } from 'wizards/models/base/base-models/plan-storages-model';
import { WhereBackupStepValue } from 'wizards/models/where-backup-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { StepBase } from 'wizards/steps/StepBase.class';
import { CREATE_BACKUP_DESTINATION } from 'wizards/tokens/create-backup-destination.token';
import { WhereBackupStepStorageListComponent } from './storage-list/where-backup-step-storage-list.component';
import { WhereBackupStepLogic } from './where-backup-step.logic';

const WhereBackupStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => WhereBackupStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-where-backup-step',
  templateUrl: './where-backup-step.component.html',
  providers: [WhereBackupStepValueAccessor],
  imports: [
    AlertComponent,
    ButtonComponent,
    CommonModule,
    FormGroupComponent,
    LoaderComponent,
    RadioComponent,
    ReactiveFormsModule,
    I18NextPipe,
    WhereBackupStepStorageListComponent
  ]
})
export class WhereBackupStepComponent extends StepBase<WhereBackupStepValue> implements OnInit, OnChanges {
  @Input() Storages: Storages = null;
  @Output() changeStorage = new EventEmitter();
  @Output() hybridChangedToDefault = new EventEmitter();

  public ability = inject(AbilityService);
  public backupFacade = inject(ComputerBackupFacade);
  public i18nPipe = inject(I18NextPipe);
  public mainService = inject(RemoteManagementWizardsService);
  private cdr = inject(ChangeDetectorRef);
  private injector = inject(Injector);
  public createDestinationService = inject(CREATE_BACKUP_DESTINATION, { optional: true });

  public readonly elementsSelector = {
    id: {
      listLocalStorages: 'list-local-storages',
      listLocalOrCloudStorages: 'list-local-or-cloud-storages'
    },
    name: {
      configNotSupportedAlert: 'configNotSupportedAlert',
      noStoragesAlert: 'noStoragesAlert',
      needNewStorageAlert: 'needNewStorageAlert',
      notLocalStorageAlert: 'notLocalStorageAlert',
      radioButtonsBox: 'radioButtonsBox',
      loaderItem: 'loaderItem',
      listSelectLocalOrCloud: 'listSelectLocalOrCloud',
      listSelectLocal: 'listSelectLocal'
    }
  };
  public readonly routingPath = RoutingPath;

  public whereBackupStepLogic: WhereBackupStepLogic;

  public creatingDestination = false;
  public isNotValidFirstTable = false;
  public isNotValidHybridTable = false;

  public headers = WhereBackupStepLogic.headers;

  get secondTableMaxHeight(): string {
    return this.isRDMode || this.isNBF || !this.whereBackupStepLogic.canBeHybrid || !this.whereBackupStepLogic.existAllStorages
      ? '336px'
      : '193px';
  }

  constructor() {
    super();
    this.whereBackupStepLogic = new WhereBackupStepLogic(this.mainService, this.backupFacade, this.i18nPipe, this);
  }

  ngOnInit(): void {
    this.whereBackupStepLogic.init();

    this.initForm();
  }

  initForm(): void {
    this.stepForm = this.whereBackupStepLogic.getForm();

    this.initFormEvents();
  }

  onStepFormChange(value: WhereBackupStepValue): void {
    this.whereBackupStepLogic.updateSelectedItems(value.HybridID, value.ConnectionID);

    if (this.stepForm.dirty || this.stepForm.touched) {
      const hybridNotEmpty = value.HybridID && value.HybridID !== GuidEmpty;

      this.isNotValidFirstTable = !value.ConnectionID || value.ConnectionID === GuidEmpty;
      this.isNotValidHybridTable =
        this.stepForm.get('IsHybridBackup').value && (this.whereBackupStepLogic.LocalStorages.length == 0 || !hybridNotEmpty);

      const isStorageValid = hybridNotEmpty || !this.isNotValidFirstTable;
      const valid = !this.isNotValidHybridTable && this.stepForm.valid && isStorageValid;

      this.value = { ...value, valid };
    }
  }

  updateForm(value: WhereBackupStepValue): void {
    if (this.isRDMode) {
      if (value.IsHybridBackup || (value.HybridID && value.HybridID !== GuidEmpty)) {
        value.IsHybridBackup = false;
        value.HybridID = GuidEmpty;
        this.importedPlanChanged = true;
        this.hybridChangedToDefault.emit(true);
      }

      if (this.isEdit && value.ConnectionID === this.value.ConnectionID) {
        this.changeSelectedHandler([value.ConnectionID], true);
      }
    }

    this.stepForm.reset(value);

    if (value.IsHybridBackup)
      this.whereBackupStepLogic.LocalStorages.forEach(
        (storage: StorageConnection) => (storage.disabled = this.whereBackupStepLogic.needHideHybridControls)
      );
  }

  forceValid(): void {
    this.stepForm.markAllAsTouched();
    this.stepForm.updateValueAndValidity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.Storages && changes.Storages.currentValue && !this.whereBackupStepLogic.MixStorages.length) {
      this.whereBackupStepLogic.setStorages(changes.Storages.currentValue);

      this.stepForm && this.stepForm.updateValueAndValidity();

      if (this.stepForm && !this.whereBackupStepLogic.existAllStorages) this.stepForm.get('IsHybridBackup').reset(false);
    }
  }

  public createDestination(): void {
    if (this.creatingDestination) return;

    this.creatingDestination = true;

    // Resolved lazily: the facade's dependency chain is wired only in mbs-ui, and RD mode is unreachable elsewhere
    const created$ = this.isRDMode
      ? this.createDestinationService
          .openForAllCompanies()
          .pipe(
            switchMap((accountId) =>
              accountId
                ? this.whereBackupStepLogic.applyCreatedDestinationForRD(this.injector.get(RemoteDeployDataFacade), accountId)
                : of(null)
            )
          )
      : this.whereBackupStepLogic
          .snapshotDestinationIds(this.injector)
          .pipe(
            switchMap((knownIds) =>
              this.createDestinationService
                .open({ hid: this.mainService.hid })
                .pipe(
                  switchMap((created) => (created ? this.whereBackupStepLogic.applyCreatedDestination(this.injector, knownIds) : of(null)))
                )
            )
          );

    created$
      .pipe(
        finalize(() => {
          this.creatingDestination = false;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe((createdId: string | null) => {
        if (!createdId) return;

        this.changeSelectedHandler([createdId], true);
        this.cdr.markForCheck();
      });
  }

  changeSelectedHandler(selectedStorage: string[], firstTable = true): void {
    this.whereBackupStepLogic.changeSelectedHandler(selectedStorage, firstTable);

    if ((!this.isEdit || !firstTable) && this.value?.IsHybridBackup)
      this.whereBackupStepLogic.disableFieldsInLists(selectedStorage[0], firstTable);
  }

  clickHandler(): void {
    if (!this.stepForm.dirty) {
      this.stepForm.markAsDirty();
      this.stepForm.updateValueAndValidity();
    }
  }

  detectChanges(): void {
    this.cdr.detectChanges();
  }
}
